/**
 * Date/month extraction rules for this codebase:
 *
 * - **DATE columns** (`Task.dueDate`, `PileEntry.date`) are stored at UTC midnight,
 *   so their intended calendar date *is* the UTC date. Extract with UTC getters.
 * - **Timestamp columns** (`Task.completedAt`, `createdAt`, `WorkSession.startedAt`)
 *   are instants; the calendar day/month they belong to is the **server-local** one.
 *   Extract with local getters.
 * - **"now"/"today"** is always server-local.
 */

// --- DATE columns (stored at UTC midnight; the UTC date IS the calendar date) ---

export function dateIsoFromDateColumn(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function monthKeyFromDateColumn(d: Date): string {
  return d.toISOString().slice(0, 7);
}

export function dateIsoFromDateColumnOrNull(d: Date | null | undefined): string | null {
  if (!d) return null;
  return dateIsoFromDateColumn(d);
}

// --- Timestamps (instants; belong to the server-local calendar day) ---

export function localDateIso(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function localMonthKey(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// --- Rounding (used by every hours/percent display) ---

export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function round4(n: number): number {
  return Math.round(n * 10_000) / 10_000;
}

/** Local wall-clock time as HH:MM. */
export function localTimeHHMM(d = new Date()): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Whether a due date (+ optional time) is past now in server-local time. */
export function isDueOverdue(
  dueDate: string | null,
  dueTime: string | null,
  todayIso = localDateIso(),
  nowHHMM = localTimeHHMM(),
): boolean {
  if (!dueDate) return false;
  if (dueDate < todayIso) return true;
  if (dueDate === todayIso && dueTime) return dueTime < nowHHMM;
  return false;
}

/** Sort key for tasks on the same calendar day — timed first, then all-day. */
export function compareDueTime(aTime: string | null, bTime: string | null): number {
  if (!aTime && !bTime) return 0;
  if (!aTime) return 1;
  if (!bTime) return -1;
  return aTime.localeCompare(bTime);
}

/** Task due label for lists/notifications, e.g. `06 June` or `06 June, 14:30`. */
export function formatTaskDueLabel(dueDate: string | null, dueTime: string | null = null): string {
  if (!dueDate) return '';
  const [y, m, d] = dueDate.split('-').map(Number);
  if (!y || !m || !d) return dueDate;
  const date = new Date(y, m - 1, d);
  const month = date.toLocaleString('en-GB', { month: 'long' });
  const day = String(d).padStart(2, '0');
  const label = `${day} ${month}`;
  const time = dueTime?.trim();
  return time ? `${label}, ${time}` : label;
}

/** Wrap a due time in `<strong>` when it appears in notification copy. */
export function emphasizeDueTimeInText(text: string, dueTime: string | null | undefined): string {
  const time = dueTime?.trim();
  if (!time || !text.includes(time)) return text;
  return text.replace(time, `<strong>${time}</strong>`);
}
